// MeydanGirisi.js — DEP/DEST KAPISI + TANIMSIZ MEYDAN BEYANI (21 Eyl 2026)
//
// 🔑 Serkan (19 Eyl 2026): *"Bir meydan girilir DEP ve DEST hanesine, girilen
//    meydan sistemde olmadigi icin uyari verir 'bu meydan sistemde yok' diye:
//    kisi bakar acaba yanlis mi yazdim diye. Eger yanlislik yok ise orda bulunan
//    ADD ARPRT butonuna basar, meydanin ICAO kodunu yazar, adini yazar ve
//    meydanin UTC saatini yazar (yaz veya kis neyse onu bu isi yapan kisi yapar,
//    SORUMLULUK ONDADIR), meydan sunset ve sunrise saatlerini de yazar. Bu meydan
//    eklenir, planlama cikar (NOT IN AD DATABASE remarki ile beraber), ucus
//    yapilir, BU MEYDAN DATABASE'E GIRMEZ."*
// ve: *"bence eklemeye gerek yok, bizde 17 bin havalimani var."*
// ve: *"havacilikta saatler UTC'dir, lokal gereken durumlarda UTC'den cekilir."*
//
// 🔴 NEDEN KAPI GEREKLI: 11 Eyl'de goreve `LEDM` yazilmisti, `LEMD` olacakti.
//    Ucus arsivden gelince rota hicbir planli sektore uymadi ve kayit
//    `match_review`e dustu — yani YANLIS YAZIM, o gunun FTL saatlerini sessizce
//    eksik biraktiran bir zincir baslatiyor. Kisi once "yanlis mi yazdim" diye
//    bakabilsin diye uyari alanin ALTINDA cikar.
//
// 🔑 TEK BILESEN, BES ALAN CIFTI (Ilke 3): tarama gosterdi ki DEP/DEST
//    FTLPanel'de BES ayri yerde yaziliyor (ASSIGN · EDIT planli · EDIT
//    gerceklesen · DH x2) — CLAUDE.md "uc" diyordu. Kapiyi bes yere ayri ayri
//    yazmak, birini unutmanin garantisiydi.
import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import HarfliGiris from './HarfliGiris';
// Maskeler TEK dosyada (Ilke 3) — bilesen kendi saat/fark maskesini yazmaz.
import { normTime, normOffset } from './inputFormat';
import { usePencereKapat, KapatX } from './pencereKapat';   // pencere kapatma tek tip — ESC + ✕ (12 Eki 2026)

const C = { border:'var(--border)', bg2:'var(--bg2)', bg3:'var(--bg3)',
            t1:'var(--t1)', t3:'var(--t3)', red:'var(--red)', orange:'var(--orange)',
            accent:'var(--accent)' };

// ICAO → true (var) / false (yok). Oturum boyunca durur: ayni meydan bes alanda
// da sorulur, her tusta sorgu atmak anlamsiz. `airports` KURESEL ve degismiyor
// (yenilemesi elle takas, 12 Tem'de bir kez yapildi) — onbellek bayatlamaz.
const bellek = new Map();

/** Meydan `airports` tablosunda var mi + saat dilimi — TEK SORGU, belleğe yazılır.
 *  { var: true|false, tz: 'Europe/London'|null, ad, ulke } · sorgu düşerse null (bilinmiyor).
 *  1 Eki 2026: OFF'un yeri ve meydan nöbeti dilimi BURADAN alır (LocationPicker) — ikinci bir arama yazılmaz.
 *  21 Eki 2026 (W4): süper admin şirket künyesinin ana üssü de (ad + ülke) buradan — kendi sorgusu ağ hatasını "yok" sayıyordu. */
export async function meydanBilgisi(icao) {
  const k = String(icao || '').toUpperCase();
  if (!/^[A-Z]{4}$/.test(k)) return null;          // sorulacak sey yok
  if (bellek.has(k)) return bellek.get(k);
  // `.limit(1)` — `maybeSingle` DEGIL: depodaki oteki meydan/ana us okumalari da
  // boyle (AdminPanel, FlightReport) ve tek desen kalsin.
  const { data, error } = await supabase.from('airports').select('icao,name,iso_country,tz').eq('icao', k).limit(1);
  // 🔴 AG HATASINI "YOK" SAYMAYIZ: sorgu duserse cevabi BILMIYORUZ demektir.
  //    `false` donseydi internet kesildiginde her meydan tanimsiz gorunur ve
  //    kullanici olmayan bir sorunu duzeltmeye calisirdi (Ilke 1).
  if (error) return null;
  const satir = Array.isArray(data) ? data[0] : data;
  const bilgi = { var: !!satir, tz: satir?.tz || null, ad: satir?.name || null, ulke: satir?.iso_country || null };
  bellek.set(k, bilgi);
  return bilgi;
}

/** Meydan `airports` tablosunda var mi? true · false · null (bilinmiyor). */
export async function meydanVarMi(icao) {
  const b = await meydanBilgisi(icao);
  return b ? b.var : null;
}

/** Testler icin — onbellegi bosaltir. */
export function meydanBellegiSifirla() { bellek.clear(); }

/** "+03:00" / "-05:30" → dakika. Gecersizse null. */
export function farkDakika(s) {
  const m = /^([+-])(\d{1,2}):([0-5]\d)$/.exec(String(s || '').trim());
  if (!m) return null;
  const dk = Number(m[2]) * 60 + Number(m[3]);
  const isaretli = m[1] === '-' ? -dk : dk;
  // Gercek dilim araligi: -12:00 .. +14:00 (semadaki CHECK ile ayni).
  return isaretli >= -720 && isaretli <= 840 ? isaretli : null;
}

/** dakika → "+03:00" (ekranda okunsun diye). */
export function dakikaFark(dk) {
  if (!Number.isFinite(dk)) return '';
  const i = dk < 0 ? '-' : '+';
  const a = Math.abs(dk);
  return `${i}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

const SAAT_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export default function MeydanGirisi({
  value, onChange, beyan = null, onBeyan = null, declaredBy = null,
  style, placeholder, disabled = false, readOnly = false, 'data-testid': testId, 'data-zorunlu': zorunlu,
}) {
  const kod = String(value || '').toUpperCase();
  const tam = /^[A-Z]{4}$/.test(kod);
  const beyanli = !!(beyan && beyan[kod]);
  const [durum, setDurum] = useState(null);        // true var · false yok · null bilinmiyor
  const [modal, setModal] = useState(false);

  useEffect(() => {
    let dead = false;
    if (!tam) { setDurum(null); return; }
    meydanVarMi(kod).then(r => { if (!dead) setDurum(r); });
    return () => { dead = true; };
  }, [kod, tam]);

  // Uyari YALNIZ kesin bilgiyle cikar: `durum === false`. `null` (bilinmiyor /
  // ag hatasi) sessizdir — olmayan bir hatayi gostermek yanlis alarmdir.
  const tanimsiz = tam && durum === false;

  return (
    <span style={{ display:'inline-block', verticalAlign:'top', width: style?.width || '100%' }}>
      <HarfliGiris
        style={style} maxLength={4} placeholder={placeholder} disabled={disabled}
        readOnly={readOnly} data-testid={testId} data-zorunlu={zorunlu}
        value={value} onChange={onChange} />

      {tanimsiz && !beyanli && (
        <span style={{ display:'block', marginTop:3, fontSize:9, letterSpacing:0.4,
                       color:C.red, fontFamily:'var(--mono)', fontWeight:700 }}
              data-testid="meydan-yok" role="alert">
          NOT IN AIRPORT DATABASE
          {!readOnly && onBeyan && (
            <button type="button" data-testid="add-arprt"
              onClick={() => setModal(true)}
              style={{ marginLeft:6, padding:'1px 6px', fontSize:9, fontWeight:700,
                       letterSpacing:0.5, cursor:'pointer', color:C.accent,
                       background:'transparent', border:`1px solid ${C.accent}`,
                       borderRadius:3, fontFamily:'var(--mono)' }}>
              ADD ARPRT
            </button>
          )}
        </span>
      )}

      {tanimsiz && beyanli && (
        <span style={{ display:'block', marginTop:3, fontSize:9, letterSpacing:0.4,
                       color:C.orange, fontFamily:'var(--mono)', fontWeight:700 }}
              data-testid="meydan-beyanli"
              title={`${beyan[kod].name} · UTC${dakikaFark(beyan[kod].utc_offset_min)}`}>
          NOT IN AD DATABASE — DECLARED
          {!readOnly && onBeyan && (
            <button type="button" data-testid="edit-arprt" onClick={() => setModal(true)}
              style={{ marginLeft:6, padding:'1px 6px', fontSize:9, fontWeight:700,
                       letterSpacing:0.5, cursor:'pointer', color:C.t3,
                       background:'transparent', border:`1px solid ${C.border}`,
                       borderRadius:3, fontFamily:'var(--mono)' }}>
              EDIT
            </button>
          )}
        </span>
      )}

      {modal && (
        <BeyanModal
          icao={kod} mevcut={beyan?.[kod] || null} declaredBy={declaredBy}
          onClose={() => setModal(false)}
          onSave={(kayit) => { onBeyan(kod, kayit); setModal(false); }} />
      )}
    </span>
  );
}

/** ADD ARPRT — o UCUSA OZEL beyan. `airports` tablosuna YAZMAZ. */
export function BeyanModal({ icao, mevcut, declaredBy, onClose, onSave }) {
  const [ad, setAd] = useState(mevcut?.name || '');
  const [fark, setFark] = useState(mevcut ? dakikaFark(mevcut.utc_offset_min) : '');
  const [kutup, setKutup] = useState(!!mevcut && !mevcut.sunrise_utc && !mevcut.sunset_utc);
  const [dogus, setDogus] = useState(mevcut?.sunrise_utc || '');
  const [batis, setBatis] = useState(mevcut?.sunset_utc || '');
  const [hata, setHata] = useState(null);
  usePencereKapat(onClose);   // iç içe: EDIT DUTY / ASSIGN / TRAINING içinde açılır — ESC yalnız bunu kapatır

  const kaydet = () => {
    if (!ad.trim()) return setHata('AERODROME NAME REQUIRED');
    const dk = farkDakika(fark);
    if (dk == null) return setHata('UTC OFFSET must be like +03:00 or -05:30');
    if (!kutup) {
      if (!SAAT_RE.test(dogus)) return setHata('SUNRISE must be HH:MM (UTC)');
      if (!SAAT_RE.test(batis)) return setHata('SUNSET must be HH:MM (UTC)');
    }
    onSave({
      name: ad.trim(), utc_offset_min: dk,
      // Kutup gunu/gecesinde saat UYDURULMAZ: null kalir ve gece/gunduz
      // "olculemedi" doner (Ilke 1). Bos dize DEGIL — sema onu reddediyor.
      sunrise_utc: kutup ? null : dogus,
      sunset_utc:  kutup ? null : batis,
      declared_by: declaredBy || null,
      declared_at: new Date().toISOString(),
    });
  };

  const alan = { width:'100%', padding:'7px 9px', background:C.bg3, color:C.t1,
                 border:`1px solid ${C.border}`, borderRadius:4, fontSize:12,
                 fontFamily:'var(--mono)', boxSizing:'border-box' };
  const etiket = { display:'block', fontSize:9, letterSpacing:1, color:C.t3,
                   fontWeight:700, marginBottom:4 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:9000,
                  display:'flex', alignItems:'center', justifyContent:'center' }}
         data-testid="beyan-modal">
      <div style={{ width:440, background:C.bg2, border:`1px solid ${C.border}`,
                    borderRadius:6, padding:18, fontFamily:'var(--mono)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:4 }}>
          <div style={{ fontSize:12, fontWeight:700, letterSpacing:1, color:C.accent }}>ADD AERODROME — {icao}</div>
          <KapatX onClose={onClose} testid="beyan-x" />
        </div>
        <div style={{ fontSize:10, color:C.t3, lineHeight:1.6, marginBottom:14 }}>
          This aerodrome is not in the database and <b>will not be added to it</b>.
          The data below applies to this duty only and is entered again for each
          flight — so summer/winter time never goes stale. All times are UTC;
          <b> the person entering them is responsible for them.</b>
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={etiket}>AERODROME NAME *</label>
          <HarfliGiris style={alan} value={ad} onChange={setAd} data-testid="beyan-ad" />
        </div>

        <div style={{ marginBottom:10 }}>
          <label style={etiket}>UTC OFFSET * (e.g. +03:00)</label>
          <input style={alan} value={fark} placeholder="+03:00" data-testid="beyan-fark"
                 onChange={e => setFark(normOffset(e.target.value))} />
          <div style={{ fontSize:9, color:C.t3, marginTop:3 }}>
            Whatever is in force on the day of the flight — summer or winter.
          </div>
        </div>

        <label style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10,
                        fontSize:10, color:C.t1, cursor:'pointer' }}>
          <input type="checkbox" checked={kutup} data-testid="beyan-kutup"
                 onChange={e => setKutup(e.target.checked)} />
          Polar day or polar night — the sun does not rise or set
        </label>

        {!kutup && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <div>
              <label style={etiket}>SUNRISE UTC *</label>
              <input style={alan} value={dogus} placeholder="05:42" data-testid="beyan-dogus"
                     onChange={e => setDogus(normTime(e.target.value))} />
            </div>
            <div>
              <label style={etiket}>SUNSET UTC *</label>
              <input style={alan} value={batis} placeholder="18:14" data-testid="beyan-batis"
                     onChange={e => setBatis(normTime(e.target.value))} />
            </div>
          </div>
        )}
        {kutup && (
          <div style={{ fontSize:10, color:C.orange, marginBottom:10, lineHeight:1.6 }}>
            Day/night will read <b>NOT MEASURED</b> for this aerodrome — no time is
            invented for it.
          </div>
        )}

        {hata && (
          <div style={{ fontSize:10, color:C.red, fontWeight:700, marginBottom:10 }}
               data-testid="beyan-hata" role="alert">{hata}</div>
        )}

        <div style={{ display:'flex', gap:9, justifyContent:'flex-end' }}>
          <button type="button" onClick={onClose} data-testid="beyan-iptal"
            style={{ padding:'7px 14px', fontSize:10, fontWeight:700, letterSpacing:1,
                     cursor:'pointer', color:C.t1, background:'transparent',
                     border:`1px solid ${C.border}`, borderRadius:4 }}>CANCEL</button>
          <button type="button" onClick={kaydet} data-testid="beyan-kaydet"
            style={{ padding:'7px 16px', fontSize:10, fontWeight:700, letterSpacing:1,
                     cursor:'pointer', color:'#08131f', background:C.accent,
                     border:'none', borderRadius:4 }}>SAVE DECLARATION</button>
        </div>
      </div>
    </div>
  );
}
